import React from 'react'
import { PaletteCard } from './PirateCard'
import { ACTION_TYPES, CONTROL_TYPES, getCardDef } from './cardDefs'
import type { CardType, ControlType } from './engine'

interface CardPaletteProps {
  /**
   * Restrict which cards appear. Action cards use plain type keys ('roast', 'mash', 'fry').
   * Control cards use param-specific keys ('for:2', 'for:3', 'while:4', etc.) in activity
   * mode, or plain type keys ('for', 'while', 'ifelse') for backward compatibility.
   * Defaults to all cards when undefined.
   */
  allowedCards?: string[]
  /**
   * In activity mode each control card is expanded into one palette entry per
   * param value — "For 2 times", "For 3 times", etc. — with no dropdown on the
   * placed card. In simulator mode (default) a single card with a dropdown is shown.
   */
  activityMode?: boolean
}

interface ControlEntry {
  type: CardType
  kind: 'action' | 'control'
  param: number
  label: string
  description: string
}

function buildActivityControlEntry(type: ControlType, param: number): ControlEntry {
  switch (type) {
    case 'for':
      return { type, kind: 'control', param, label: `For ${param} times`, description: `Repeat the body ${param} times.` }
    case 'while':
      return { type, kind: 'control', param, label: `While > ${param} 🥔`, description: `Repeat while target has more than ${param} potatoes.` }
    case 'ifelse':
      return { type, kind: 'control', param, label: `If ≤ ${param} 🥔`, description: `If target has ${param} or fewer potatoes: IF branch, else: ELSE branch.` }
  }
}

/** Draggable source list of cards, grouped into Attacks and Controls. */
export function CardPalette({ allowedCards, activityMode = false }: CardPaletteProps) {
  // Action cards: keyed by plain type ('roast', 'mash', 'fry')
  const isActionAllowed = (t: CardType) => !allowedCards || allowedCards.includes(t)
  // Control cards: keyed by 'type:param' in new format, or plain 'type' for backward compat
  const isControlAllowed = (t: CardType) => {
    if (!allowedCards) return true
    if (allowedCards.includes(t)) return true  // old format
    if (activityMode) {
      const params = getCardDef(t).paramOptions ?? []
      return params.some(p => allowedCards.includes(`${t}:${p}`))
    }
    return false
  }
  const isParamAllowed = (t: CardType, param: number) =>
    !allowedCards || allowedCards.includes(`${t}:${param}`) || allowedCards.includes(t)

  const actions = ACTION_TYPES.filter(isActionAllowed)
  const controls = CONTROL_TYPES.filter(isControlAllowed)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {actions.length > 0 && (
        <div>
          <h4 style={sectionTitle}>⚔️ Action Cards</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {actions.map((t) => (
              <PaletteCard key={t} type={t} kind={getCardDef(t).kind} />
            ))}
          </div>
        </div>
      )}
      {controls.length > 0 && (
        <div>
          <h4 style={sectionTitle}>🔁 Control Cards</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activityMode
              ? controls.flatMap((t) => {
                  const def = getCardDef(t)
                  return (def.paramOptions ?? [])
                    .filter(param => isParamAllowed(t, param))
                    .map((param) => {
                      const entry = buildActivityControlEntry(t as ControlType, param)
                      return (
                        <PaletteCard
                          key={`${t}-${param}`}
                          type={entry.type}
                          kind={entry.kind}
                          param={entry.param}
                          overrideLabel={entry.label}
                          overrideDescription={entry.description}
                        />
                      )
                    })
                })
              : controls.map((t) => (
                  <PaletteCard key={t} type={t} kind={getCardDef(t).kind} />
                ))}
          </div>
        </div>
      )}
      <p style={{ fontSize: 11, color: 'var(--pp-text-muted)', margin: 0, lineHeight: 1.4 }}>
        {activityMode
          ? 'Tap a card to add it to your ship.'
          : 'Drag cards under your ships. Drop an attack inside a control card to repeat or branch it.'}
      </p>
    </div>
  )
}

const sectionTitle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--color-text-muted)',
  margin: '0 0 8px',
}
